/**
 * <s-sticky-buy> — Mobile sticky buy bar for the product page.
 *
 * Shows itself once the main buy buttons scroll out of view (above the viewport)
 * and hides again when they come back. The bar never owns cart logic: its
 * button clicks the real add-to-cart button inside the product form, so the
 * currently selected variant, quantity and sold-out state always apply. When
 * the main button is unavailable (sold out), it scrolls the shopper back to the
 * product options instead.
 *
 * Required markup:
 *   [data-sticky-price]   — Inside self; receives the current price text.
 *   [data-sticky-button]  — Inside self; the bar's action button.
 *
 * External coupling (read fresh on every use because the product-form partial is re-rendered):
 *   [data-product-form]                    — Wrapper observed for visibility.
 *   [data-product-form] button[name="add"] — Real add-to-cart submit button.
 *   [data-product-price]                   — Main price element to mirror.
 */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-button]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.target = document.querySelector('[data-product-form]');
    if (!this.target || !this.button) return;

    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);

    this.observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggle(scrolledPast);
    });
    this.observer.observe(this.target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.button?.removeEventListener('click', this.onClick);
  }

  toggle(show) {
    if (show) this.syncPrice();
    this.toggleAttribute('data-visible', show);
    this.inert = !show;
  }

  syncPrice() {
    const price = document.querySelector('[data-product-price]');
    if (price && this.priceEl) this.priceEl.replaceChildren(...[...price.childNodes].map((node) => node.cloneNode(true)));
  }

  onClick() {
    const addButton = this.target.querySelector('button[name="add"]');
    if (addButton && addButton.getAttribute('aria-disabled') !== 'true' && !addButton.disabled) {
      addButton.click();
    } else {
      this.target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', StickyBuy);
